import React from 'react'
import { Link } from 'react-router-dom'
import { BookOpen, FileText, Video, Code, ArrowRight } from 'lucide-react'
import { PublicFooter } from '../components/layout/PublicFooter'
import { PublicNavigation } from '../components/layout/PublicNavigation'
import { Button } from '../components/ui/Button'

export const DocumentationPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 relative overflow-hidden transition-colors duration-300">
      <PublicNavigation />

      {/* Hero Section */}
      <section className="py-20 lg:py-32 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#69adba]/10 dark:bg-[#69adba]/20 rounded-full mb-6">
              <BookOpen className="h-8 w-8 text-[#69adba]" />
            </div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900 dark:text-slate-100 sm:text-6xl mb-6">
              Documentation & Guides
            </h1>
            <p className="mt-6 text-lg leading-8 text-slate-600 dark:text-slate-400">
              Everything you need to know about using Open1031 for your 1031 exchanges.
            </p>
          </div>
        </div>
      </section>

      {/* Documentation Sections */}
      <section className="py-20 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Getting Started */}
            <div className="bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 hover:shadow-xl transition-shadow">
              <div className="h-12 w-12 rounded-xl bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center mb-4">
                <FileText className="h-6 w-6 text-[#69adba]" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
                Getting Started
              </h3>
              <p className="text-slate-600 dark:text-slate-400 mb-4">
                Learn the basics of setting up your account and starting your first exchange.
              </p>
              <Link to="/documentation/getting-started" className="text-[#69adba] hover:text-[#5a9aa6] dark:hover:text-[#7bb8c5] font-medium flex items-center">
                Read Guide <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>

            {/* API Documentation */}
            <div className="bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 hover:shadow-xl transition-shadow">
              <div className="h-12 w-12 rounded-xl bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center mb-4">
                <Code className="h-6 w-6 text-[#69adba]" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
                API Documentation
              </h3>
              <p className="text-slate-600 dark:text-slate-400 mb-4">
                Integrate Open1031 with your existing systems using our comprehensive API.
              </p>
              <Link to="/documentation/api" className="text-[#69adba] hover:text-[#5a9aa6] dark:hover:text-[#7bb8c5] font-medium flex items-center">
                View API Docs <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>

            {/* Video Tutorials */}
            <div className="bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 hover:shadow-xl transition-shadow">
              <div className="h-12 w-12 rounded-xl bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center mb-4">
                <Video className="h-6 w-6 text-[#69adba]" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
                Video Tutorials
              </h3>
              <p className="text-slate-600 dark:text-slate-400 mb-4">
                Watch step-by-step video guides to master the platform quickly.
              </p>
              <Link to="/documentation/video-tutorials" className="text-[#69adba] hover:text-[#5a9aa6] dark:hover:text-[#7bb8c5] font-medium flex items-center">
                Watch Videos <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>

            {/* Exchange Process */}
            <div className="bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 hover:shadow-xl transition-shadow">
              <div className="h-12 w-12 rounded-xl bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center mb-4">
                <FileText className="h-6 w-6 text-[#69adba]" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
                Exchange Process
              </h3>
              <p className="text-slate-600 dark:text-slate-400 mb-4">
                Detailed guides on completing each phase of your 1031 exchange.
              </p>
              <Link to="/documentation/exchange-process" className="text-[#69adba] hover:text-[#5a9aa6] dark:hover:text-[#7bb8c5] font-medium flex items-center">
                Learn More <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>

            {/* Best Practices */}
            <div className="bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 hover:shadow-xl transition-shadow">
              <div className="h-12 w-12 rounded-xl bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center mb-4">
                <BookOpen className="h-6 w-6 text-[#69adba]" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
                Best Practices
              </h3>
              <p className="text-slate-600 dark:text-slate-400 mb-4">
                Tips and strategies for successful 1031 exchanges from industry experts.
              </p>
              <Link to="/documentation/best-practices" className="text-[#69adba] hover:text-[#5a9aa6] dark:hover:text-[#7bb8c5] font-medium flex items-center">
                Read Guide <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>

            {/* Troubleshooting */}
            <div className="bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 hover:shadow-xl transition-shadow">
              <div className="h-12 w-12 rounded-xl bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center mb-4">
                <FileText className="h-6 w-6 text-[#69adba]" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
                Troubleshooting
              </h3>
              <p className="text-slate-600 dark:text-slate-400 mb-4">
                Common issues and solutions to help you resolve problems quickly.
              </p>
              <Link to="/documentation/troubleshooting" className="text-[#69adba] hover:text-[#5a9aa6] dark:hover:text-[#7bb8c5] font-medium flex items-center">
                View Solutions <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#69adba] dark:bg-[#5a9aa6]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl mb-4">
            Need More Help?
          </h2>
          <p className="mt-4 text-lg text-white/90 mb-8">
            Our support team is here to assist you with any questions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/support">
              <Button className="bg-white text-[#69adba] hover:bg-slate-50">
                Contact Support
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/faq">
              <Button variant="outline" className="border-white text-white hover:bg-white/10">
                View FAQ
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  )
}

